import { Box, BoxProps } from "@mui/material";
import Grid, { GridProps } from "@mui/material/Grid"; // Grid version 2
import { BREAKPOINT_VALUES, PAGE_CONTAINER } from "../../const";
import { useScreenSize } from "../../hooks";

/**
 * Caps for each container size. These live in content space — a Container sits
 * inside the page's main area, which is already inset by the nav rail — so they
 * stay in step with the rail-offset breakpoints without needing an offset of
 * their own: `large` hits its 1312px cap at exactly the window width where the
 * theme starts reporting `lg`.
 *
 * `small` is deliberately tighter than the `sm` breakpoint (560 vs 672); the rest
 * mirror their breakpoint.
 */
const CONTAINER_MAX_WIDTHS = {
    small: 560,
    medium: BREAKPOINT_VALUES.md,
    large: BREAKPOINT_VALUES.lg,
    extraLarge: BREAKPOINT_VALUES.xl,
};

export type TContainerSize = keyof typeof CONTAINER_MAX_WIDTHS;
export interface IContainer extends BoxProps {
    /**
     * Controls the component's width (default: **extraLarge**):
     *
     * - **small**: 560px
     * - **medium**: 1056px
     * - **large**: 1312px
     * - **extraLarge**: 1584px (default)
     */
    size?: TContainerSize;
    /**
     * Declares the container as a CSS query container so descendants can size themselves against it.
     * It's named PAGE_CONTAINER and descendants can use it like a media query.
     */
    queryContainer?: boolean;
}

export const Container = ({
    size = "large",
    queryContainer = false,
    children,
    style,
    sx,
    ...rest
}: IContainer) => (
    <Box
        {...rest}
        sx={{
            width: {
                xs: "calc(100% - 2rem)",
                sm: "calc(100% - 4rem)",
            },
            margin: "0 auto",
            maxWidth: `${CONTAINER_MAX_WIDTHS[size]}px`,
            ...(queryContainer && {
                containerType: "inline-size",
                containerName: PAGE_CONTAINER,
            }),
            ...style,
            ...sx,
        }}
    >
        {children}
    </Box>
);

interface IRowProps extends GridProps {
    /** Array of three column-count sizes for different screen sizes, from larger to smaller. ex: ["xl | lg | md", "sm", "xs"] or [12, 8, 4] */
    dynamicCols?: [number, number, number];
}
export const Row: React.FC<IRowProps> = ({ dynamicCols = [16, 8, 4], children, ...rest }) => {
    const { screenSize } = useScreenSize();

    const getColumnCount = () => {
        switch (screenSize) {
            case "xs":
                return dynamicCols[2];
            case "sm":
                return dynamicCols[1];
            default:
                return dynamicCols[0];
        }
    };

    return (
        <Grid container columns={getColumnCount()} {...rest}>
            {children}
        </Grid>
    );
};

type GridSizeValue = number | "auto" | "grow" | boolean;

interface IColProps extends Omit<GridProps, "size"> {
    xs?: GridSizeValue;
    sm?: GridSizeValue;
    md?: GridSizeValue;
    lg?: GridSizeValue;
    xl?: GridSizeValue;
}

const convertSize = (value: GridSizeValue | undefined): number | "auto" | "grow" | undefined => {
    if (value === true) return "grow";
    if (value === false) return undefined;
    return value as number | "auto" | "grow" | undefined;
};

export const Col = ({ children, xs, sm, md, lg, xl, ...rest }: IColProps) => (
    <Grid
        size={{
            xs: convertSize(xs),
            sm: convertSize(sm),
            md: convertSize(md),
            lg: convertSize(lg),
            xl: convertSize(xl),
        }}
        {...rest}
    >
        {children}
    </Grid>
);
